import { useState } from 'react';
import { Heading } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { INPUT_REQUEST_MAX_LENGTH, INPUT_REQUEST_MIN_LENGTH } from '@complan/contracts';
import { AriaModalDialog } from '@/shared/a11y/AriaDialog';
import { Button } from '@/components/ui/button';
import { TextArea } from '@/features/initiative/intake/fields';

/**
 * The Request input dialog (R2.6, R2.11): a Read_Only_User's one action against
 * an Initiative they may read. The text is 1 to 2,000 characters; a violation is
 * reported by the server as `validation_failed` and rendered here as a plain
 * error, since a single free-text field has nothing to diff or merge — the user
 * simply edits and resubmits with their text intact (the dialog never clears it
 * on failure).
 *
 * A true dialog (focus trap, focus restore, `Esc`) via {@link AriaModalDialog}.
 */
export function InputRequestDialog({
  isOpen,
  isSubmitting,
  hasError,
  onSubmit,
  onClose,
}: {
  readonly isOpen: boolean;
  readonly isSubmitting: boolean;
  readonly hasError: boolean;
  readonly onSubmit: (text: string) => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [text, setText] = useState('');

  const trimmedLength = text.trim().length;
  const isValid = trimmedLength >= INPUT_REQUEST_MIN_LENGTH && text.length <= INPUT_REQUEST_MAX_LENGTH;

  return (
    <AriaModalDialog
      variant="centre"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      aria-label={t('detail.inputRequestDialog.title')}
    >
      <Heading slot="title" className="text-[15px] font-semibold text-txt">
        {t('detail.inputRequestDialog.title')}
      </Heading>
      <p className="text-muted-foreground mt-2 text-[12.3px]">{t('detail.inputRequestDialog.body')}</p>

      {hasError ? (
        <p className="mt-2 text-[12px] text-[#8E2A1E]">{t('detail.inputRequestDialog.error')}</p>
      ) : null}

      <div className="mt-3 flex flex-col gap-1">
        <label htmlFor="input-request-text" className="text-[11.5px] font-semibold text-txt">
          {t('detail.inputRequestDialog.textLabel')}
        </label>
        <TextArea
          id="input-request-text"
          value={text}
          maxLength={INPUT_REQUEST_MAX_LENGTH}
          onChange={(event) => setText(event.target.value)}
        />
        <p className="text-muted-foreground text-[11px]">{t('detail.inputRequestDialog.textHint')}</p>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
          {t('detail.inputRequestDialog.cancel')}
        </Button>
        <Button
          size="sm"
          disabled={!isValid || isSubmitting}
          onClick={() => onSubmit(text.trim())}
        >
          {isSubmitting ? t('detail.inputRequestDialog.submitting') : t('detail.inputRequestDialog.submit')}
        </Button>
      </div>
    </AriaModalDialog>
  );
}
